import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { Post, PostMedia } from "@/adapters/api";
import { Skeleton } from "@/components/ui/skeleton.tsx";

function formatOccurredAt(value: string): string {
	const date = parseISO(value);
	return isValid(date)
		? format(date, "d MMM · HH:mm", { locale: ptBR })
		: value;
}

function thumbnailOf(media: PostMedia): string | undefined {
	if (media.type === "image") return media.url;
	if (media.type === "video") return media.thumbnail_url;
	return undefined;
}

interface Thumbnail {
	id: string;
	url: string;
	caption: string | undefined;
}

function RecentPostMedia({ media }: { media: PostMedia[] }) {
	const thumbnails = media
		.map((item) => ({
			id: item.id,
			url: thumbnailOf(item),
			caption: item.caption,
		}))
		.filter((item): item is Thumbnail => item.url !== undefined);

	if (thumbnails.length === 0) return null;

	return (
		<ul className="recent-post__media">
			{thumbnails.map((thumbnail) => (
				<li key={thumbnail.id}>
					<img
						src={thumbnail.url}
						alt={thumbnail.caption ?? "Mídia do relato"}
						loading="lazy"
						className="recent-post__media-img"
					/>
				</li>
			))}
		</ul>
	);
}

export interface RecentPostsProps {
	posts?: Post[];
	isPending: boolean;
	isError: boolean;
}

const POST_SKELETONS = ["primeiro", "segundo", "terceiro"];

export function RecentPosts({ posts, isPending, isError }: RecentPostsProps) {
	if (isPending) {
		return (
			<ul className="recent-posts">
				{POST_SKELETONS.map((key) => (
					<li key={key}>
						<Skeleton className="recent-post-skeleton" />
					</li>
				))}
			</ul>
		);
	}

	if (isError) {
		return (
			<p className="dashboard-panel-error" role="status">
				Não foi possível carregar os relatos.
			</p>
		);
	}

	if (!posts || posts.length === 0) {
		return (
			<p className="dashboard-panel-empty" role="status">
				Nenhum relato publicado ainda.
			</p>
		);
	}

	return (
		<ul className="recent-posts">
			{posts.map((post) => (
				<li key={post.id} className="recent-post">
					<p className="recent-post__title">{post.title}</p>
					<p className="recent-post__meta">
						{post.author.removed
							? "Usuário removido"
							: (post.author.name ?? "Usuário removido")}
						{" · "}
						{formatOccurredAt(post.occurred_at)}
					</p>
					{post.body && <p className="recent-post__body">{post.body}</p>}
					<RecentPostMedia media={post.media} />
				</li>
			))}
		</ul>
	);
}
